import {
  type ChangeEvent,
  type KeyboardEvent,
  useCallback,
  useMemo,
} from "react";
import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import type { StreamSearchCriteriaState } from "@/lib/utils/streamSearchUtils";
import {
  type SearchFilterKey,
  useStreamSearchFormState,
} from "@/features/study-group-stream/hooks/useStreamSearchFormState";
import { CLEAR_AUTHOR_VALUE } from "@/features/study-group-stream/utils/streamSearchFilterOptions";
import type { SearchAuthorOption } from "@/features/study-group-stream/utils/streamSearchFilterOptions";
import type { SearchQueryFieldProps } from "@/features/study-group-stream/components/StreamSearchQueryField";
import type { SearchFiltersPanelProps } from "@/features/study-group-stream/components/StreamSearchFiltersPanel";
import type { SearchResultHeaderProps } from "@/features/study-group-stream/components/StreamSearchResultHeader";
import { buildSearchResultQueryText } from "@/features/study-group-stream/utils/streamSearchResultsHelpers";

interface UseStreamSearchFormViewModelOptions {
  onSearch: (searchCriteriaState: StreamSearchCriteriaState) => void;
  onExitSearch: () => void;
  totalHits?: number;
  authorOptions: SearchAuthorOption[];
  authorLabelById: Map<string, string>;
}

export type StreamSearchFormViewModel = {
  queryFieldProps: SearchQueryFieldProps;
  filtersPanelProps: Omit<SearchFiltersPanelProps, "onSearch" | "onExitSearch">;
  filterPanelActions: Pick<
    SearchFiltersPanelProps,
    "onSearch" | "onExitSearch"
  >;
  resultHeaderProps: SearchResultHeaderProps;
};

const buildSearchLabels = (t: TFunction) => ({
  placeholder: t("streamSearch.placeholderActive"),
  inputAriaLabel: t("streamSearch.search"),
  clearSelectionLabel: t("common.clearSelection"),
  startDateLabel: t("common.startDateForFilter"),
  endDateLabel: t("common.endDateForFilter"),
  authorLabel: t("streamSearch.author"),
  allAuthorsLabel: t("streamSearch.allAuthors"),
  closeSearchLabel: t("streamSearch.closeSearch"),
  searchLabel: t("common.search"),
  resultLabel: t("streamSearch.resultLabel"),
});

const buildFilterHandlers = (
  handleFilterChange: (key: SearchFilterKey) => (value: string) => void,
  handleFilterClear: (key: SearchFilterKey) => () => void,
) => ({
  onFromChange: handleFilterChange("from"),
  onToChange: handleFilterChange("to"),
  onAuthorChange: handleFilterChange("authorId"),
  onFromClear: handleFilterClear("from"),
  onToClear: handleFilterClear("to"),
  onAuthorClear: handleFilterClear("authorId"),
});

export const useStreamSearchFormViewModel = ({
  onSearch,
  onExitSearch,
  totalHits = 0,
  authorOptions,
  authorLabelById,
}: UseStreamSearchFormViewModelOptions): StreamSearchFormViewModel => {
  const { t } = useTranslation();
  const {
    query,
    filters,
    lastSearchedCriteriaState,
    hasSearched,
    searchCriteriaState,
    setQuery,
    submitSearch,
    clearQuery,
    updateFilter,
    clearFilter,
  } = useStreamSearchFormState({ onSearch });

  const getAuthorLabel = useCallback(
    (authorId: string): string | undefined => authorLabelById.get(authorId),
    [authorLabelById],
  );

  const handleQueryInputChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const nextQuery = e.target.value;
      setQuery(nextQuery);
    },
    [setQuery],
  );

  const handleFilterChange = useCallback(
    (key: SearchFilterKey) => (value: string) => {
      const nextValue =
        key === "authorId" && value === CLEAR_AUTHOR_VALUE ? "" : value;
      updateFilter(key, nextValue);
    },
    [updateFilter],
  );

  const handleFilterClear = useCallback(
    (key: SearchFilterKey) => () => clearFilter(key),
    [clearFilter],
  );

  const handleSearchKeyDown = useCallback(
    (e: KeyboardEvent<HTMLElement>) => {
      if (e.key !== "Enter") return;
      if (e.nativeEvent.isComposing || e.defaultPrevented) return;
      e.preventDefault();
      submitSearch();
    },
    [submitSearch],
  );

  const filterHandlers = useMemo(
    () => buildFilterHandlers(handleFilterChange, handleFilterClear),
    [handleFilterChange, handleFilterClear],
  );
  const { hasCriteria: hasSearchCriteria } = searchCriteriaState;
  const { criteria: lastSearchedCriteria } = lastSearchedCriteriaState;

  const noConditionText = t("streamSearch.noCondition");
  const lastSearchedText = hasSearched
    ? buildSearchResultQueryText({
        t,
        searchParams: lastSearchedCriteria,
        getAuthorLabel,
      })
    : noConditionText;

  const labels = useMemo(() => buildSearchLabels(t), [t]);

  return useMemo(
    () => ({
      queryFieldProps: {
        query,
        placeholder: labels.placeholder,
        inputAriaLabel: labels.inputAriaLabel,
        onQueryChange: handleQueryInputChange,
        onQueryKeyDown: handleSearchKeyDown,
        onQueryClear: clearQuery,
        clearLabel: labels.clearSelectionLabel,
      },
      filtersPanelProps: {
        from: filters.from || "",
        to: filters.to || "",
        authorId: filters.authorId || CLEAR_AUTHOR_VALUE,
        hasSearchCriteria,
        authorOptions,
        startDateLabel: labels.startDateLabel,
        endDateLabel: labels.endDateLabel,
        authorLabel: labels.authorLabel,
        allAuthorsLabel: labels.allAuthorsLabel,
        closeSearchLabel: labels.closeSearchLabel,
        searchLabel: labels.searchLabel,
        clearSelectionLabel: labels.clearSelectionLabel,
        onFromChange: filterHandlers.onFromChange,
        onToChange: filterHandlers.onToChange,
        onAuthorChange: filterHandlers.onAuthorChange,
        onFromClear: filterHandlers.onFromClear,
        onToClear: filterHandlers.onToClear,
        onAuthorClear: filterHandlers.onAuthorClear,
        onFilterKeyDown: handleSearchKeyDown,
      },
      filterPanelActions: {
        onSearch: submitSearch,
        onExitSearch,
      },
      resultHeaderProps: {
        visible: hasSearched,
        queryText: lastSearchedText,
        resultLabel: labels.resultLabel,
        resultCountLabel: t("streamSearch.resultCount", {
          count: totalHits,
        }),
        noConditionText,
      },
    }),
    [
      clearQuery,
      filters.authorId,
      filters.from,
      filters.to,
      authorOptions,
      filterHandlers,
      hasSearched,
      labels,
      handleQueryInputChange,
      handleSearchKeyDown,
      lastSearchedText,
      noConditionText,
      onExitSearch,
      query,
      hasSearchCriteria,
      submitSearch,
      t,
      totalHits,
    ],
  );
};
